import {useEffect, useRef} from "react";
import {Editor, Element, Transforms} from "slate";
import * as SlateReact from "slate-react";

import {IntegerField} from "@khanacademy/event-schemas";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {getLastInteractionId} from "@khan/guide-core";
import {useMathEditorContext} from "@khan/guide-math-editor-context";
import {useGuideState} from "@khan/guide-state";
import {RequestInfo} from "@khan/shared-core";

import {useAudioSpeechToText} from "../use-audio-speech-to-text.ts";
import {useSpeechRecognition} from "../use-speech-recognition.ts";

export interface SpeechToTextResult {
    /** Whether or not the recognition server is listening to incoming audio */
    on: boolean;
    /** Whether the audio is recognized as speech */
    transcribing: boolean;
    /** Method for toggling on/off the speech recognition service */
    toggleSpeechRecognition: () => void;
    /**
     * The result of the last recognition event. Usually corresponds to a
     * 'continuous' stream of speech.
     */
    lastTranscription: string;
    /** The interim result of the last recognition event. Typically before
     * a final transcription is returned, we will return interim results which
     * can be used to provide a "live" transcription experience.
     */
    interimTranscription: string;
    /** Whether the browser currently supports Speech-to-text. */
    isSupportedBrowser: boolean;
}

export const useSpeechToText = () => {
    const editor = SlateReact.useSlate();
    const isMobile = RequestInfo.isMobile;

    // We prefer native browser speech recognition. See each hook for
    // documentation.
    const browserSpeech = useSpeechRecognition();
    const audioSpeech = useAudioSpeechToText();
    const speech = browserSpeech.isSupportedBrowser
        ? browserSpeech
        : audioSpeech;

    const showMicButton = !isMobile && speech.isSupportedBrowser;
    const {isAnyActive: isAnyMathActive} = useMathEditorContext();

    const conversation = useGuideState("conversation");
    const navigation = useGuideState("navigation");
    const threadId = useGuideState("threadId");
    const lastLoggedTranscriptionRef = useRef<string>("");

    // Effect to handle when a math input becomes active while speech
    // recognition is on.
    useEffect(() => {
        // If the speech recognition is on, then we want to disable the
        // speech-to-text for the math editor.
        if (isAnyMathActive && speech.on) {
            speech.toggleSpeechRecognition();
        }
    }, [isAnyMathActive, speech]);

    // Effect to handle speech-to-text final transcription results
    useEffect(() => {
        // The last transcription represents the result of transcribing a
        // continuous stream of speech. We want to insert this into the editor
        // at the current selection point.
        if (speech.lastTranscription) {
            editor.insertText(speech.lastTranscription);
        }
    }, [editor, speech.lastTranscription]);

    // Effect to handle intermediary speech-to-text transcription results.
    // This is used to update the editor with the current transcription as
    // the user is speaking. This is not the final transcription result and
    // will eventually be replaced by the final result.
    useEffect(() => {
        if (speech.interimTranscription) {
            // We'll insert the interim transcription at the current selection
            // point. If the user is currently in a math input, then we'll
            // set the current selection to the end of the editor to avoid
            // breaking the math input.
            let startPoint = editor.selection?.anchor ?? Editor.end(editor, []);
            const [node] = Editor.above(editor, {at: startPoint}) ?? [];

            if (Element.isElement(node) && node.type === "math-editor") {
                startPoint = Editor.end(editor, []);
            }
            editor.insertText(speech.interimTranscription);
            // We'll update the current selection so that the next interim
            // transcription or final result will replace the text that was
            // just inserted.
            Transforms.setSelection(editor, {
                anchor: startPoint,
                focus: editor.selection?.focus ?? Editor.end(editor, []),
            });
        }
    }, [editor, speech.interimTranscription]);

    // Effect to log transcription results.
    useEffect(() => {
        if (
            !speech.lastTranscription ||
            speech.lastTranscription === lastLoggedTranscriptionRef.current
        ) {
            return;
        }

        // We store the logged transcription in a ref to avoid resending
        // events if other effect dependencies change.
        lastLoggedTranscriptionRef.current = speech.lastTranscription;
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideSpeechToTextTranscribed",
            eventSchemaVersion: 1,
            aiGuideThreadId: threadId ?? "",
            aiGuideNavigationId: navigation.id,
            aiGuideInteractionId: getLastInteractionId(conversation),
            userAgent: window.navigator.userAgent,
            speechToTextImplementation:
                speech === browserSpeech
                    ? "SPEECH_RECOGNITION"
                    : "AUDIO_SPEECH_TO_TEXT",
            numWordsInTranscription: new IntegerField(
                countWords(speech.lastTranscription),
            ),
        });
    }, [browserSpeech, conversation, navigation.id, speech, threadId]);

    // Effect to reset the logged transcription ref when transcribing starts.
    useEffect(() => {
        if (speech.transcribing) {
            lastLoggedTranscriptionRef.current = "";
        }
    }, [speech.transcribing]);

    return {
        speechInProgress: speech.on && speech.transcribing,
        showMicButton,
        speech,
    };
};

function countWords(str: string) {
    const trimmed = str.trim();
    return trimmed === "" ? 0 : trimmed.split(/\s+/).length;
}
